import { ArrowUpDown, ArrowUp, ArrowDown } from "lucide-react";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { TableHead } from "@/components/ui/table";
import type { HeroKeywordsSortField } from "@/types";

type SortDirection = "asc" | "desc";

interface SortIconProps {
  field: HeroKeywordsSortField;
  sortField: HeroKeywordsSortField;
  sortDirection: SortDirection;
}

export function SortIcon({ field, sortField, sortDirection }: SortIconProps) {
  if (sortField !== field) return <ArrowUpDown className="h-3 w-3 opacity-50" />;
  return sortDirection === "asc" ? (
    <ArrowUp className="h-3 w-3" />
  ) : (
    <ArrowDown className="h-3 w-3" />
  );
}

interface SortableHeaderProps {
  field: HeroKeywordsSortField;
  label: string;
  tooltip: string;
  className?: string;
  sortField: HeroKeywordsSortField;
  sortDirection: SortDirection;
  disableSortInteractions: boolean;
  onSortChange: (field: HeroKeywordsSortField) => void;
}

export function SortableHeader({
  field,
  label,
  tooltip,
  className,
  sortField,
  sortDirection,
  disableSortInteractions,
  onSortChange,
}: SortableHeaderProps) {
  return (
    <TableHead
      className={cn(
        "py-2.5 px-2 text-center font-bold text-sm cursor-pointer select-none",
        className,
        disableSortInteractions && "opacity-70 cursor-not-allowed",
      )}
      onClick={() => {
        if (disableSortInteractions) return;
        onSortChange(field);
      }}
    >
      <div className="flex items-center justify-center gap-1">
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger
              className="min-w-0 text-center cursor-help border-b border-dotted border-muted-foreground"
              onClick={(e) => e.preventDefault()}
            >
              {label}
            </TooltipTrigger>
            <TooltipContent side="bottom">
              <p>{tooltip}</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
        <div className="shrink-0">
          <SortIcon field={field} sortField={sortField} sortDirection={sortDirection} />
        </div>
      </div>
    </TableHead>
  );
}
